import { useEffect, useEffectEvent, useRef } from "react";

const SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileOptions {
  sitekey: string;
  callback: (token: string) => void;
  "expired-callback"?: () => void;
  "error-callback"?: () => void;
}

interface TurnstileApi {
  render: (el: HTMLElement, opts: TurnstileOptions) => string;
  reset: (id?: string) => void;
  remove: (id: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<void> | null = null;

/** Loads the official Turnstile script once (shared across hook instances). */
function loadScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error("Failed to load Turnstile script."));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
}

interface UseTurnstileArgs {
  sitekey: string;
  onToken: (token: string) => void;
  onExpire: () => void;
}

/**
 * Renders the Turnstile widget into the returned `containerRef`. `onToken` is
 * called with the token after a successful challenge, `onExpire` on expiry or
 * error. `reset()` clears the widget (e.g. after a failed form submission).
 */
export function useTurnstile({ sitekey, onToken, onExpire }: UseTurnstileArgs) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const handleToken = useEffectEvent(onToken);
  const handleExpire = useEffectEvent(onExpire);

  useEffect(() => {
    // Bez site key nemá co renderovat – nestahuj ani skript.
    if (!sitekey) return;
    let cancelled = false;
    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey,
          callback: handleToken,
          "expired-callback": handleExpire,
          "error-callback": handleExpire,
        });
      })
      .catch(handleExpire);
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [sitekey]);

  const reset = () => {
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current);
    }
  };

  return { containerRef, reset };
}
